/**
 * Block 12 calculator — `corpusQ`: количество листов ЛДСП на корпус.
 *
 * 7 per-anketa variants + top-level dispatcher через discriminated union
 * `CalculatorParams.type`. Anketa `'other'` (Другая мебель) — формула не
 * применима (throws).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.X Б.3 / §4.3.
 */

import type { CalculatorParams } from '../../contracts/calculator';
import { mmToM } from '../utils/mmToM';
import {
  assertPositiveDimension,
  type BalconyParams,
  type BathCabinetParams,
  type BathShelfAboveParams,
  type BathTabletopParams,
  type CabinetParams,
  type KitchenParams,
  type TvZoneParams,
} from './types';

/**
 * Кухня: `corpusQ = Math.ceil(L_м × 1)`.
 *
 * **Источник:** xlsx `Кухня!C29`:
 *   «Программа считает Количество листов ЛДСП для корпус. Это количество
 *    равно количеству метров длины кухни. Например, длина кухни 5 метров,
 *    значит на корпус необходимо 5 листов ЛДСП... (Всегда принимаем целое
 *    значение, округляем в большую сторону)»
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 Б.3 + §4.3.
 *
 * @example
 *   corpusQKitchen({ type: 'kitchen', length: 5000, ... }) // 5
 *   corpusQKitchen({ type: 'kitchen', length: 4200, ... }) // 5 (ceil(4.2))
 */
export function corpusQKitchen(params: KitchenParams): number {
  assertPositiveDimension(params.length, 'corpusQKitchen.length');
  return Math.ceil(mmToM(params.length));
}

/**
 * Шкаф / Гардероб: `corpusQ = Math.ceil(L_м × 2)`.
 *
 * **Источник:** xlsx `Шкаф!C27`. Пример L=2000 → `ceil(4) = 4 листа`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.2 Б.3 + §4.3.
 *
 * @example
 *   corpusQCabinet({ type: 'cabinet', length: 2000, ... }) // 4
 *   corpusQCabinet({ type: 'cabinet', length: 1500, ... }) // 3 (ceil(3))
 */
export function corpusQCabinet(params: CabinetParams): number {
  assertPositiveDimension(params.length, 'corpusQCabinet.length');
  return Math.ceil(mmToM(params.length) * 2);
}

/**
 * Сан узел тумба со столешницей: ladder per `hasWashingMachine` (OQ-6).
 *
 * **Источник:** xlsx `Тумба-ванна!C27`/`D27` + методика §2.3 Б.3.
 *
 * | Условие L (мм)  | washing=false | washing=true |
 * | --------------- | ------------: | -----------: |
 * | L < 1200        |             1 |        — (1) |
 * | 1200 ≤ L < 1800 |             2 |            1 |
 * | 1800 ≤ L < 2000 |             2 |            2 |
 * | 2000 ≤ L < 2200 |             3 |            2 |
 * | L ≥ 2200        |             3 |            3 |
 *
 * Для L < 1200 c washing=true методика говорит «не достижим» (физически),
 * но возвращаем 1 — defensive default (стиральная машина может уместиться
 * только в шкаф ≥ 1200 мм, но формула не должна крашиться на edge).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.3 Б.3.
 */
export function corpusQBathTabletop(params: BathTabletopParams): number {
  assertPositiveDimension(params.length, 'corpusQBathTabletop.length');
  const L = params.length;
  if (params.hasWashingMachine) {
    if (L < 1800) return 1;
    if (L < 2200) return 2;
    return 3;
  }
  if (L < 1200) return 1;
  if (L < 2000) return 2;
  return 3;
}

/**
 * Сан узел шкаф над антресолью: `corpusQ = Math.ceil(L_м × 0.5)`.
 *
 * **Источник:** xlsx `Антресоль!C28`. Пример L=1500 → `ceil(0.75) = 1 лист`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.4 Б.3 + §4.3.
 *
 * @example
 *   corpusQBathShelfAbove({ ..., length: 1500 }) // 1
 *   corpusQBathShelfAbove({ ..., length: 2100 }) // 2 (ceil(1.05))
 */
export function corpusQBathShelfAbove(params: BathShelfAboveParams): number {
  assertPositiveDimension(params.length, 'corpusQBathShelfAbove.length');
  return Math.ceil(mmToM(params.length) * 0.5);
}

/**
 * Сан узел шкаф: `corpusQ = Math.ceil(L_м × 1)`.
 *
 * **NB:** corpusQ **НЕ зависит** от стиралки/сушилки (только areaFacade
 * имеет вычеты −0.6 / −1.2). Источник: xlsx `Шкаф-санузел!C28`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.5 Б.3 + §4.3.
 *
 * @example
 *   corpusQBathCabinet({ ..., length: 2500 }) // 3 (ceil(2.5))
 *   corpusQBathCabinet({ ..., length: 1000 }) // 1
 */
export function corpusQBathCabinet(params: BathCabinetParams): number {
  assertPositiveDimension(params.length, 'corpusQBathCabinet.length');
  return Math.ceil(mmToM(params.length));
}

/**
 * Внутренний ladder для тумбы ТВ-зоны (xlsx новый 14.05.2026 — OQ-11 ИСПРАВЛЕН).
 *
 * | tumbaL (мм)     | corpusQ |
 * | --------------- | ------: |
 * | L < 1000        |       1 |
 * | 1000 ≤ L < 2000 |       2 |
 * | 2000 ≤ L < 3000 |       3 |
 * | 3000 ≤ L < 4000 |       4 |
 * | L ≥ 4000        |       4 (cap — практически не бывает) |
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.6 Б.3.
 */
function corpusQTvZoneTumba(tumbaL: number): number {
  if (tumbaL < 1000) return 1;
  if (tumbaL < 2000) return 2;
  if (tumbaL < 3000) return 3;
  return 4;
}

/**
 * Внутренний ladder для шкафа в ТВ-зоне.
 *
 * | shelfL (мм)     | corpusQ |
 * | --------------- | ------: |
 * | L < 500         |       1 |
 * | 500 ≤ L < 1000  |       2 |
 * | 1000 ≤ L < 1600 |       3 |
 * | 1600 ≤ L < 2400 |       4 |
 * | L ≥ 2400        |       4 (cap) |
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.6 Б.3.
 */
function corpusQTvZoneShelf(shelfL: number): number {
  if (shelfL < 500) return 1;
  if (shelfL < 1000) return 2;
  if (shelfL < 1600) return 3;
  return 4;
}

/**
 * ТВ-зона: `corpusQ = ladder(tumbaL) + (hasShelf ? ladder(shelfL) : 0)`.
 *
 * `length` в `TvZoneParams` = tumba L. `shelfLength` (опц.) — длина шкафа,
 * учитывается только если `hasShelf=true` И `shelfLength` определён.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.6 Б.3.
 *
 * @example
 *   corpusQTvZone({ ..., length: 2500, hasShelf: false })           // 3
 *   corpusQTvZone({ ..., length: 2500, hasShelf: true, shelfLength: 1200 }) // 3 + 3 = 6
 */
export function corpusQTvZone(params: TvZoneParams): number {
  assertPositiveDimension(params.length, 'corpusQTvZone.length');
  const tumbaPart = corpusQTvZoneTumba(params.length);
  if (params.hasShelf && typeof params.shelfLength === 'number') {
    assertPositiveDimension(params.shelfLength, 'corpusQTvZone.shelfLength');
    return tumbaPart + corpusQTvZoneShelf(params.shelfLength);
  }
  return tumbaPart;
}

/**
 * Балкон: `corpusQ = Math.ceil(L_м × 3)` (прямой) или
 * `Math.ceil((L_м + L2_м − 0.6) × 3)` (угловой).
 *
 * Угловой определяется наличием `length2` (per методика §2.7 + OQ-13).
 * Контракт `BalconyParamsSchema` не имеет `isAngular` явно — Phase B3 может
 * добавить, но для B2 inference от `length2` достаточно.
 *
 * **Источник:** xlsx `Балкон!C10` (новая версия 14.05.2026 + OQ-13).
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.7 Б.2 + §4.3.
 *
 * @example
 *   corpusQBalcony({ ..., length: 1000 })                  // 3
 *   corpusQBalcony({ ..., length: 1500, length2: 1000 })   // 6 (ceil((1.5+1-0.6)*3) = 6)
 *   corpusQBalcony({ ..., length: 2000 })                  // 6
 */
export function corpusQBalcony(params: BalconyParams): number {
  assertPositiveDimension(params.length, 'corpusQBalcony.length');
  const isAngular = typeof params.length2 === 'number';
  if (isAngular) {
    assertPositiveDimension(params.length2, 'corpusQBalcony.length2');
    const totalL_m = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalL_m <= 0) {
      throw new Error(
        `corpusQBalcony: angular total length must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
    return Math.ceil(totalL_m * 3);
  }
  return Math.ceil(mmToM(params.length) * 3);
}

/**
 * Top-level dispatcher: выбирает вариант формулы по `params.type`.
 *
 * `'other'` (Другая мебель) — свободный режим без формул, throws explicit
 * error (template engine Phase B3 не вызовет corpusQ для 'other').
 *
 * @throws Error для `type='other'` — формула не применима.
 */
export function corpusQ(params: CalculatorParams): number {
  switch (params.type) {
    case 'kitchen':
      return corpusQKitchen(params);
    case 'cabinet':
      return corpusQCabinet(params);
    case 'bath-tabletop':
      return corpusQBathTabletop(params);
    case 'bath-shelf-above':
      return corpusQBathShelfAbove(params);
    case 'bath-cabinet':
      return corpusQBathCabinet(params);
    case 'tv-zone':
      return corpusQTvZone(params);
    case 'balcony':
      return corpusQBalcony(params);
    case 'other':
      throw new Error('corpusQ: не применяется к "Другая мебель" (свободный режим)');
  }
}
